import { useState, useEffect, useCallback } from "react";
import { useSnackbar } from "notistack";
import useWidthHook from "../useWidthHook";
import { getErrMsg, ADAPTER_URL, BRIDGE_EMAIL, IS_VERIFIED_TEACHER, ADAPTER_PREFIX, postApi, getApi, getObjectValue } from "@cti-workspace/helpers";

const useBridgeHook = ({ organizations, connectedCredsData, setConnectedCredsData }) => {
	const name = "Bridge";
	const type = "bridge";
	const { enqueueSnackbar } = useSnackbar();
	const [importedCourses, setImportedCourses] = useState(null);
	const [importedModules, setImportedModules] = useState(null);
	const [loggedInBridgeEmail, setloggedInBridgeEmail] = useState("");
	const [loggedInProvider, setLoggedInProvider] = useState("");
	const [selectedCourse, setSelectedCourse] = useState({});
	const [selectedCourseWork, setSelectedCourseWork] = useState({});
	const [selectedCredential, setSelectedCredential] = useState({});
	const [verifiedTeacher, setVerifiedTeacher] = useState(false);
	const currentWidth = useWidthHook();
	const [showLoadMore, setShowLoadMore] = useState({ items: [] });

	const isAuthorized = (response) => {
		if (response === "Unauthorized") {
			enqueueSnackbar("Your session has timed out... Please login again.", {
				variant: "error",
			});
			logout();
			return false;
		}
		return true;
	};

	useEffect(() => {
		const email = localStorage.getItem(BRIDGE_EMAIL);
		const isVerifiedTeacher = localStorage.getItem(IS_VERIFIED_TEACHER);
		if (email) {
			setloggedInBridgeEmail(email);
			setLoggedInProvider("bridge");
		}
		if (isVerifiedTeacher == "true") setVerifiedTeacher(true);
	}, []);

	const connectItemWithCredential = useCallback(async (courseWork, credential, course) => {
		try {
			// Course work and credential passed in if using the automated single click flow
			const courseWorkToConnect = courseWork || selectedCourseWork;
			const credentialToConnect = credential || selectedCredential;
			const courseToConnect = course || selectedCourse;
			const courseId = courseToConnect.id.toString();
			const courseWorkId = courseWorkToConnect.id.toString();
			// const courseWorkId = "ALL";
			const email = localStorage.getItem(BRIDGE_EMAIL);

			// Save the binding of the LMS event with the credential
			let request = {
				email,
				credDefId: credentialToConnect._id,
				orgId: organizations.default._id,
				courseId,
				courseName: courseToConnect.title,
				courseWorkId,
				courseWorkName: courseWorkToConnect.title,
				lmsType: type,
			};

			const url = "/adapter/saveConnectedCredential";
			const response = await postApi(url, request);

			setConnectedCredsData([...connectedCredsData, response.credential]);
			// Clear the selections
			setSelectedCourseWork({});
			setSelectedCredential({});

			// Now start listening for events
			request = {
				courseId,
				courseWorkId,
				email,
			};

			//Registering to feed not required on canvas hense commented out
			// const data = await postApi(`/googleClassroom/registerToFeed`, request, {}, true, ADAPTER_URL);

			enqueueSnackbar(`Success!`, {
				variant: "Success",
			});
		} catch (error) {
			enqueueSnackbar(getErrMsg(null, "Could not register to feed... please contact contact@cti3.com"), {
				variant: "error",
			});
		}
	});

	const deleteConnectedCredential = useCallback(async (courseId, courseWorkId) => {
		try {
			const request = {
				courseId,
				courseWorkId,
			};
			const url = "/adapter/deleteConnectedCredential";
			await postApi(url, request);
			setConnectedCredsData(connectedCredsData.filter((cred) => !(cred.courseId === courseId && cred.courseWorkId === courseWorkId)));
			enqueueSnackbar(`Success!`, {
				variant: "Success",
			});
		} catch (error) {
			enqueueSnackbar(getErrMsg(null, "Could not be deleted... please contact contact@cti3.com"), {
				variant: "error",
			});
		}
	});

	const getCourseWork = useCallback(async (course) => {
		try {
			const data = await getApi(`/googleClassroom/listCourseWork/${course.id}/${loggedInBridgeEmail}`, {}, true, ADAPTER_URL);
			if (isAuthorized(data) && data) {
				return data;
			} else {
				return false;
			}
		} catch (error) {
			console.error({ error });
		}
	});

	const importCourses = useCallback(async () => {
		try {
			const data = await getApi(`/bridge/listCourses/${loggedInBridgeEmail}`, {}, true, ADAPTER_URL);
			if (isAuthorized(data)) {
				getCoursesPerPage(1, 1, "", true);
				return true;
			} else {
				return false;
			}
		} catch (error) {
			enqueueSnackbar(getErrMsg(null, "Courses could not be downloaded at this time..."), {
				variant: "error",
			});
		}
	});

	const reimportCourses = useCallback(async () => {
		try {
			const data = await getApi(`/bridge/reimportCourses/${loggedInBridgeEmail}`, {}, true, ADAPTER_URL);
			if (isAuthorized(data)) {
				getCoursesPerPage(1, 1, "", true);
				return true;
			} else {
				return false;
			}
		} catch (error) {
			enqueueSnackbar(getErrMsg(null, "Courses could not be downloaded at this time..."), {
				variant: "error",
			});
		}
	});

	/**
	 * @TODO - Depreceted Method, To be removed in Future
	 */
	// const importCourses = useCallback(async () => {
	// 	try {
	// 		const data = await getApi(`/bridge/listCourses/${loggedInBridgeEmail}`, {}, true, ADAPTER_URL);
	// 		if (isAuthorized(data) && data && data.courses) {
	// 			// Do not present the archived courses for now... could also filter by state=ACTIVE...
	// 			const nonArchivedCourses = data.courses.filter((course) => course.is_published == true || course.is_published == false);
	// 			enqueueSnackbar(`Success! Downloaded ${nonArchivedCourses.length} courses.`, {
	// 				variant: "success",
	// 			});
	// 			setImportedCourses(nonArchivedCourses);
	// 			return true;
	// 		} else {
	// 			return false;
	// 		}
	// 	} catch (error) {
	// 		console.error({ error });
	// 		enqueueSnackbar("Courses could not be downloaded at this time...", {
	// 			variant: "error",
	// 		});
	// 	}
	// });

	const importCourseWork = useCallback(async (course) => {
		try {
			setSelectedCourse(course);

			const data = await getApi(`/canvas/listCourseModules/${course.id}/${loggedInBridgeEmail}`, {}, true, ADAPTER_URL);
			console.log(data);
			if (isAuthorized(data) && data) {
				enqueueSnackbar(`Success! Downloaded ${data.modules.length} Modules.`, {
					variant: "success",
				});
				setImportedModules(data.modules);
				return true;
			} else {
				return false;
			}
		} catch (error) {
			console.error({ error });
			enqueueSnackbar("Modules could not be downloaded at this time...", {
				variant: "error",
			});
		}
	});

	const login = useCallback(async () => {
		try {
			/**
			 * @TODO - don't do this and properly send an auth request to adapter when cors fixed
			 */
			let orgId = organizations.default._id;
			const settingsData = localStorage.getItem("settings");
			const settingsObj = JSON.parse(settingsData);

			if (!settingsObj || !settingsObj.bridgeLmsKeys || !settingsObj.bridgeLmsKeys.API_KEY) {
				enqueueSnackbar("Unfortunately this LMS is not yet connected. Please contact contact@cti3.com to learn more.", {
					variant: "error",
				});
				return;
			}

			const data = await getApi(
				`/bridge/saveAccessToken?API_KEY=${settingsObj.bridgeLmsKeys.API_KEY}
			&EMAIL=${settingsObj.bridgeLmsKeys.EMAIL}
			&orgId=${orgId}`,
				{},
				true,
				ADAPTER_URL
			);

			if ("email" in data) {
				enqueueSnackbar(`Success! Logged in as ${data.email} `, {
					variant: "success",
				});
				setloggedInBridgeEmail(data.email);
				setLoggedInProvider("bridge");
				setVerifiedTeacher(true);

				// Save to localStorage for across session auth
				localStorage.setItem(BRIDGE_EMAIL, data.email);
				localStorage.setItem(IS_VERIFIED_TEACHER, data.verifiedTeacher || false);
			}
		} catch (error) {
			enqueueSnackbar("Your login attempt to Bridge failed. Please try again or contact contact@cti3.com", {
				variant: "error",
			});
		}
	});

	const logout = useCallback(() => {
		setloggedInBridgeEmail("");
		setLoggedInProvider("");
		localStorage.removeItem(BRIDGE_EMAIL);
	});

	const saveAuthorizationCredentials = useCallback(async (queryString) => {
		try {
			const data = await getApi(`/canvas/oauth2/callback/code${queryString}`, {}, true, ADAPTER_URL);
			if ("email" in data) {
				enqueueSnackbar(`Success! Logged in as ${data.email} `, {
					variant: "success",
				});
				setloggedInBridgeEmail(data.email);
				setVerifiedTeacher(true);

				// Save to localStorage for across session auth
				localStorage.setItem(BRIDGE_EMAIL, data.email);
				localStorage.setItem(IS_VERIFIED_TEACHER, data.verifiedTeacher || false);
			}
		} catch (error) {
			console.error({ error });
			enqueueSnackbar("The authorization credentials could not be stored...", {
				variant: "error",
			});
		}
	});

	const subscribeToCourseWorkEvents = useCallback(async (selection) => {
		try {
			const email = localStorage.getItem(BRIDGE_EMAIL);
			const request = {
				courseId: selection.courseId,
				courseWorkId: selection.courseWorkId,
				email,
			};
			const data = await postApi(`/googleClassroom/registerToFeed`, request, {}, true, ADAPTER_URL);
			console.log({ data });
		} catch (error) {
			console.error({ error });
			enqueueSnackbar("The authorization credentials could not be stored...", {
				variant: "error",
			});
		}
	});

	const updateConnectedCredential = useCallback(async (_id, credentialName, imageUrl) => {
		try {
			const request = {
				_id,
				credentialName,
				imageUrl,
			};
			const url = "/adapter/updateConnectedCredential";
			await postApi(url, request);
			enqueueSnackbar(`Success!`, {
				variant: "Success",
			});
		} catch (error) {
			enqueueSnackbar("Could not be updated... please contact contact@cti3.com", {
				variant: "error",
			});
		}
	});

	const getCoursesPerPage = async (pageSize = 1, pageNumber = 1, search = "", clearCredentials = false) => {
		try {
			switch (currentWidth) {
				case "xs":
					pageSize = 10;
					break;
				case "sm":
					pageSize = 20;
					break;
				case "md":
					pageSize = 20;
					break;
				case "lg":
					pageSize = 9;
					break;
				default:
					pageSize = 12;
			}

			const email = localStorage.getItem(BRIDGE_EMAIL);

			const data = await getApi(`/${ADAPTER_PREFIX}/getLMSCourses?pageSize=${pageSize}&pageNumber=${pageNumber}&search=${search}&email=${email}&type=bridge`);
			const { totalItems, items = [], pageSize: currentPageSize, pageNumber: currentPageNumber } = data;
			enqueueSnackbar(`Success! Downloaded ${items.length} courses.`, { variant: "success" });

			if (items) {
				if (clearCredentials) {
					setImportedCourses(items);
					setShowLoadMore({ items, totalItems, currentPageNumber, currentPageSize });
				} else {
					setImportedCourses([...importedCourses, ...items]);
					setShowLoadMore({ items: [...getObjectValue(showLoadMore, "items", []), ...items], totalItems, currentPageNumber, currentPageSize });
				}
			} else {
				throw data;
			}
		} catch (error) {
			if (error[0]) {
				error.message = `Error type:${getObjectValue(error, "[0].keyword")} message: ${getObjectValue(error, "[0].message")}`;
			}
			throw error;
		}
	};

	return {
		connectItemWithCredential,
		deleteConnectedCredential,
		importCourses,
		reimportCourses,
		importedCourses,
		importCourseWork,
		importedModules,
		getCourseWork,
		loggedInBridgeEmail,
		loggedInProvider,
		logout,
		name,
		login,
		saveAuthorizationCredentials,
		setImportedCourses,
		setImportedModules,
		setSelectedCourse,
		selectedCourse,
		setSelectedCourseWork,
		selectedCourseWork,
		setSelectedCredential,
		selectedCredential,
		subscribeToCourseWorkEvents,
		type,
		updateConnectedCredential,
		verifiedTeacher,
		getCoursesPerPage,
		showLoadMore,
		setShowLoadMore,
	};
};

export default useBridgeHook;
